import {WorkAcceptance} from "../components/WorkAcceptance";
import {useSavedReport} from "../hooks/useReads";
import {useQueryClient} from "@tanstack/react-query";
// Day complete: the installer ticks the assigned scope. Photos and notes are optional.
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Button, Textarea } from "@aiw/ui";
import { useT } from "../i18n";
import { usePageHead } from "../shell/Frame";
import { useJob, useJobInstalls } from "../hooks/useReads";
import { day, time } from "../lib/format";
import { useFileReport } from "../lib/report";
import { useReportActions, useReport } from "../state/reports";
import { AnswerLine, useAnswer } from "../components/Answer";
import { Panel, Pill } from "../components/Panel";
import { PhotoButtons, PhotoGrid } from "../components/PhotoGallery";
import {sydneyDayOf,sydneyToday} from '@artemis-group/aiw-contracts';
import {ApiError} from '@aiw/api-client';
import {useRole} from '../hooks/useMe';

export function Completion() {
  const fileReport=useFileReport();
  const {updateReport}=useReportActions();
  const { ref = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { t, lang } = useT();
  const {canInstall}=useRole();
  const job = useJob(ref);
  const installs = useJobInstalls(ref);
  const report = useReport(ref);
  const installationId = job.data?.installation_id ?? installs.data?.[0]?.id;
  const saved = useSavedReport(ref, installationId);
  const partyId = saved.data?.party_id;
  const cache = useQueryClient();
  const { answer, say } = useAnswer();
  const [busy, setBusy] = useState(false);
  const [saveError,setSaveError]=useState<string|null>(null);
  const [scopeDoneDay, setScopeDoneDay] = useState<string|null>(null);

  const days = installs.data?.[0]?.days ?? [];
  const todayIdx = days.findIndex((d) => d.date===sydneyToday());
  const requestedIdx = days.findIndex((day) => day.id === searchParams.get("day"));
  const belongs=(day:typeof days[number])=>!day.assignee?.id||day.assignee.id===partyId;
  const idx = requestedIdx >= 0 ? requestedIdx : todayIdx >= 0 && belongs(days[todayIdx]!) ? todayIdx : Math.max(0, days.findIndex((day) => belongs(day)&&!day.done && !saved.data?.days_done?.[day.id??""]));
  const d = days[idx] ?? null;
  const assignedToThisInstaller=!!d&&belongs(d);
  const scopeDone = !!d?.id && scopeDoneDay === d.id;
  const dayPhotos = d ? report.photos.filter(p=>p.label===d.id||(!p.label&&sydneyDayOf(p.at)===d.date)) : [];
  const doneAt = saved.data?.days_done?.[d?.id??""] ?? d?.done ?? null;
  const allDone = days.length > 0 && days.every((x) => !!(saved.data?.days_done?.[x.id??""] ?? x.done));

  usePageHead(t("completion_title", { name: job.data?.name ?? ref }), d ? `${t("day_n_of_m", { n: idx + 1, m: days.length })} · ${day(d.date, lang)}` : null);

  if (job.isError || installs.isError || saved.isError) return <Panel><p role="alert" className="text-sm text-destructive">{t("work_load_failed")}</p><Button size="touch" className="mt-3" onClick={() => { void job.refetch(); void installs.refetch(); void saved.refetch(); }}>{t("retry_work")}</Button></Panel>;

  async function submit() {
    if (!canInstall || !scopeDone || !assignedToThisInstaller || !d?.task?.trim() || !partyId || !d?.id || !saved.data?.schedule_ready || busy) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await fileReport({ job_ref: ref, installation_id: job.data?.installation_id ?? installs.data?.[0]?.id ?? null, party_id: partyId, kind: "completion", day_id:d!.id, notes: report.notes, photos: dayPhotos.map((p) => p.path), photo_receipts:dayPhotos.map(p=>p.receipt??""), checklist: { scope_completed: scopeDone }, scope: d!.id });
      say(r);
      if(r.status === "executed") await cache.invalidateQueries({queryKey:["installer-report-state",ref]});

    } catch(e) {
      say("failed");
      setSaveError(e instanceof ApiError ? `${t('ans_server_failed')} (${e.status}: ${e.message})` : e instanceof Error && /Account changed|Sign in required/.test(e.message) ? t('ans_session_failed') : t('ans_network_failed'));
    } finally { setBusy(false); }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 md:items-start">
      <div className="space-y-4">
        <WorkAcceptance jobRef={ref} installation={installationId}/>
        {!canInstall&&<p className="text-sm text-warning">{t("onsite_access_needed")}</p>}
        {saved.data&&!saved.data.schedule_ready&&<p className="text-sm text-warning">{t("recorded_day_required")}</p>}
        <Panel title={t("todays_photos")} right={<span>{t("photos_optional")}</span>}>
          {report.photos_error&&<p role="alert" className="text-sm text-destructive">{t("photos_load_failed")}</p>}
          <PhotoGrid photos={dayPhotos} cols={3} />
          <div className="mt-3"><PhotoButtons jobRef={ref} label={d?.id??undefined} size="touch" /></div>
        </Panel>
        <Panel title={t("notes")}>
          <Textarea rows={4} value={report.notes} placeholder={t("notes_placeholder")} onChange={(e) => updateReport(ref, { notes: e.target.value })} className="text-sm" />
        </Panel>
      </div>

      <div className="space-y-4">
        <Panel title={d ? t("day_card", { n: idx + 1, date: day(d.date, lang) }) : t("btn_day_complete")} right={days.length > 0 && <Pill tone="info">{t("day_n_of_m", { n: idx + 1, m: days.length })}</Pill>}>
          {!assignedToThisInstaller&&d&&<p className="mb-2 text-sm text-warning">{t('day_other_installer',{name:d.assignee?.name??d.crew??''})}</p>}
          {d?.task?.trim() ? <label className="touch-row cursor-pointer text-sm"><input type="checkbox" className="h-5 w-5 accent-[hsl(var(--primary))]" checked={scopeDone} disabled={!canInstall||!assignedToThisInstaller} onChange={(e)=>setScopeDoneDay(e.target.checked ? d.id??null : null)} /> <span>{t("scope_completed")}: {d.task}</span></label> : <p className="text-sm text-warning">{t("scope_missing")}</p>}
          {doneAt ? (
            <Pill tone="ok" className="mt-3"><Check className="mr-1 h-3.5 w-3.5" aria-hidden />{t("day_done_sent", { n: idx + 1, time: time(doneAt, lang) })}</Pill>
          ) : (
            <Button size="touch" className="mt-3 w-full" disabled={!canInstall || !assignedToThisInstaller || !scopeDone || !d?.task?.trim() || !partyId || busy || !d?.id || !saved.data?.schedule_ready} onClick={() => void submit()}>{t("btn_day_complete")}</Button>
          )}
          <div className="mt-2"><AnswerLine answer={answer} /></div>
          {saveError&&<p role="alert" className="mt-2 text-sm text-destructive">{saveError}</p>}
          {!doneAt && <p className="mt-2 text-micro text-muted-foreground">{t("scope_tick_help")}</p>}
        </Panel>

        <Panel title={t("office_review")}>
          <p className="text-sm text-muted-foreground">{allDone?t("days_reported_office_separate"):t("office_review_separate")}</p>
        </Panel>

      </div>
    </div>
  );
}
